import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Plus, Edit2, Trash2, Upload, Loader2, Building2, ExternalLink,
  ShieldCheck, ShieldAlert, X, FileSpreadsheet, BadgeCheck
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const EMPTY = {
  name: '', logo_url: '', website: '', emails: '', country: '', city: '',
  commodities: '', principal_name: '', principal_email: '', phones: '', description: '', notes: '',
};

const AdminDirectory = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [claimFilter, setClaimFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [editing, setEditing] = useState(null); // null | {} (new) | {id,...}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.q = search;
      if (claimFilter !== 'all') params.claim_status = claimFilter;
      const res = await api.get('/admin/directory', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (e) {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, [search, claimFilter, page]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (c) => {
    setForm({
      name: c.name || '', logo_url: c.logo_url || '', website: c.website || '',
      emails: (c.emails || []).join(', '), country: c.country || '', city: c.city || '',
      commodities: (c.commodities || []).join(', '), principal_name: c.principal_name || '',
      principal_email: c.principal_email || '', phones: (c.phones || []).join(', '),
      description: c.description || '', notes: c.notes || '',
    });
    setEditing(c);
  };

  const toList = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, logo_url: form.logo_url || null, website: form.website || null,
        emails: toList(form.emails), country: form.country || null, city: form.city || null,
        commodities: toList(form.commodities), principal_name: form.principal_name || null,
        principal_email: form.principal_email || null, phones: toList(form.phones),
        description: form.description || null, notes: form.notes || null,
      };
      if (editing && editing.id) {
        await api.put(`/admin/directory/${editing.id}`, payload);
        toast.success('Company updated');
      } else {
        await api.post('/admin/directory', payload);
        toast.success('Company added');
      }
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}" from the directory?`)) return;
    try {
      await api.delete(`/admin/directory/${c.id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const doImport = async (dryRun) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Choose a file first'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/admin/directory/import?dry_run=${dryRun}`, fd);
      setImportResult(res.data);
      if (!dryRun) {
        toast.success(`Imported: ${res.data.created} new, ${res.data.updated} updated`);
        fetchItems();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Directory</h1>
            <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} companies in the global directory</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/directory-claims" className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-2">
              <BadgeCheck className="w-4 h-4" /> Claims
            </Link>
            <button onClick={() => { setShowImport(true); setImportResult(null); }} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-2">
              <Upload className="w-4 h-4" /> Import
            </button>
            <button onClick={openNew} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2" style={{ backgroundColor: BRAND_BLUE }}>
              <Plus className="w-4 h-4" /> Add Company
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-lg px-3">
            <Search className="w-4 h-4 text-gray-400" />
            <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search name, country, email..." className="w-full py-2 text-sm outline-none" />
          </div>
          <select value={claimFilter} onChange={(e) => { setPage(1); setClaimFilter(e.target.value); }} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            <option value="all">All statuses</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="pending">Pending</option>
            <option value="approved">Claimed</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/80 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Commodities</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400">No companies found</td></tr>
                ) : items.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {c.logo_url ? <img src={c.logo_url} alt="" className="w-full h-full object-contain" /> : <Building2 className="w-4 h-4 text-gray-300" />}
                        </div>
                        <div className="min-w-0">
                          <Link to={`/directory/${c.slug}`} target="_blank" className="font-medium text-gray-800 hover:underline flex items-center gap-1">
                            {c.name} <ExternalLink className="w-3 h-3 text-gray-400" />
                          </Link>
                          {c.website && <div className="text-xs text-gray-400 truncate max-w-[200px]">{c.website}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">{[c.city, c.country].filter(Boolean).join(', ') || '—'}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">
                      {(c.commodities || []).slice(0, 3).join(', ')}{(c.commodities || []).length > 3 ? '…' : ''}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium w-fit ${
                          c.claim_status === 'approved' ? 'bg-green-50 text-green-700' :
                          c.claim_status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'
                        }`}>{c.claim_status}</span>
                        <span className="inline-flex items-center gap-1 text-xs w-fit" style={{ color: c.verification?.verified ? '#C9A227' : '#9CA3AF' }}>
                          {c.verification?.verified ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                          {c.verification?.verified ? `Verified${c.verification?.tier ? ' · ' + c.verification.tier : ''}` : 'Not verified'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-5 text-sm">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-40">Prev</button>
            <span className="text-gray-600">Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">{editing.id ? 'Edit company' : 'Add company'}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
              {[
                ['name', 'Company name *'], ['logo_url', 'Logo URL'], ['website', 'Website'],
                ['emails', 'Emails (comma-separated)'], ['country', 'Country'], ['city', 'City'],
                ['commodities', 'Commodities (comma-separated)'], ['phones', 'Phones (comma-separated)'],
                ['principal_name', 'Principal name'], ['principal_email', 'Principal email'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Internal notes (never shown publicly)</label>
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-amber-50/40" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Import companies</h3>
              <button onClick={() => setShowImport(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-3">
                Upload a <strong>.xlsx</strong> or <strong>.csv</strong>. Recognized columns: Company Name, Logo, Website, Email(s),
                Country, City, Commodities, Principal Name, Principal Email, Phone(s), Description, Notes.
                Duplicates are matched by website/email domain or name+country.
              </p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700" />

              {importResult && (
                <div className="mt-4 rounded-lg bg-gray-50 border border-gray-100 p-4 text-sm">
                  <p className="font-medium text-gray-800 mb-1">{importResult.dry_run ? 'Preview (no changes saved)' : 'Import complete'}</p>
                  <ul className="text-gray-600 space-y-0.5">
                    <li>Rows read: {importResult.total_rows}</li>
                    <li className="text-green-700">New: {importResult.created}</li>
                    <li className="text-blue-700">Updated/filled: {importResult.updated}</li>
                    <li className="text-gray-500">Skipped (no name): {importResult.skipped}</li>
                  </ul>
                  {importResult.errors?.length > 0 && (
                    <details className="mt-2"><summary className="text-red-600 cursor-pointer">{importResult.errors.length} error(s)</summary>
                      <ul className="mt-1 text-xs text-red-600">{importResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => doImport(true)} disabled={importing} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-60">
                {importing ? 'Working…' : 'Preview (dry run)'}
              </button>
              <button onClick={() => doImport(false)} disabled={importing} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
              </button>
            </div>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default AdminDirectory;
